/**
 * The trip-page designs a ride manager can pick, as plain ids.
 *
 * The ids only, so it runs under plain node (tests/unit/rideDesigns.test.js).
 * What each one looks like -- its layout, its picker thumbnail, its labels --
 * is in src/designs/index.js, which is built from this list.
 *
 * Must match RIDE_PRESENTATION_DESIGNS in the backend's travelerblog/ride/views.py:
 * the server refuses an id it does not know, so a design added only here could
 * never be saved.
 */

const DESIGN_IDS = ['classic', 'poster'];

const DEFAULT_DESIGN = 'classic';

// The design to draw for a stored value. Anything unknown -- missing, from an
// older backend, or removed since -- is the default, so a bad value can never
// leave the trip page empty.
function resolveDesign(value) {
  const id = String(value || '').trim().toLowerCase();
  return DESIGN_IDS.includes(id) ? id : DEFAULT_DESIGN;
}

// One design's own settings from ride.presentation_settings, which holds a
// section per design id ({ poster: { v: 1, splits, photos } }), or null when
// that design is automatic. The backend validates each section on save and
// upgrades old ones to the current version "v" on read (PRESENTATION_SETTINGS);
// this only guards against a missing or malformed value, and against a
// version newer than the caller's code knows (version), so the page never
// breaks on one or misreads it.
function designSettings(ride, designId, version = 1) {
  const all = ride && ride.presentation_settings;
  const section = all && typeof all === 'object' ? all[designId] : null;
  if (!section || typeof section !== 'object' || Array.isArray(section)) return null;
  const v = section.v === undefined ? 1 : section.v;
  return v === version ? section : null;
}

// ride.presentation_settings after a save of sections ({ poster: {...} }, one
// key per design saved, null for one put back to automatic), as a new object;
// the designs not in sections are kept, as the server keeps them.
function withDesignSections(ride, sections = {}) {
  const all = Object.assign({}, (ride && ride.presentation_settings) || {});
  Object.keys(sections).forEach(designId => {
    if (sections[designId]) all[designId] = sections[designId];
    else delete all[designId];
  });
  return all;
}

module.exports = { DESIGN_IDS, DEFAULT_DESIGN, resolveDesign, designSettings, withDesignSections };
